import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import { useReveal } from '../components/useReveal';
import { useRef, useState } from 'react';

/* ✅ Actual ERP URL */
const ERP_URL = 'https://stgoldcovering.tech/about-developer';

const projects = [
  {
    id: 'erp',
    tag: 'React.js · ERP System',
    category: 'development',
    title: 'ST Gold Covering Company — ERP System',
    desc: 'A full ERP-level business management system built with React for ST Gold Covering Company. Streamlines operations, manages inventory, tracks workflows, and improves data management through a modern web-based interface — deployed live on VPS.',
    tech: ['React.js', 'REST API', 'MySQL', 'Node.js', 'VPS'],
    featured: true,
    img: '/photo1.png',
    gradient: 'linear-gradient(135deg, #1a1400 0%, #0e0c00 50%, #080808 100%)',
    accentColor: 'rgba(201,169,110,0.07)',
    link: ERP_URL,
    year: '2024',
    status: 'Live',
  },
  {
    id: 'ecom',
    tag: 'WordPress · E-commerce',
    category: 'ecommerce',
    title: '4× WordPress E-commerce Websites',
    desc: 'Developed and launched four fully functional e-commerce stores using WordPress and WooCommerce — including product management, payment gateway setup, and business-focused performance optimization.',
    tech: ['WordPress', 'WooCommerce', 'PHP', 'SEO'],
    featured: false,
    img: '/photo2.png',
    gradient: 'linear-gradient(135deg, #0a0a18 0%, #080810 50%, #080808 100%)',
    accentColor: 'rgba(100,100,200,0.05)',
    link: null,
    year: '2024',
    status: 'Delivered',
  },
  {
    id: 'vps',
    tag: 'DevOps · Server Management',
    category: 'devops',
    title: 'Production VPS Deployment — Hostinger',
    desc: 'Configured and deployed multiple production-grade applications on Hostinger VPS servers. Handled full Linux server setup, Nginx configuration, SSL certificates, and domain linking.',
    tech: ['Linux', 'Nginx', 'Hostinger VPS', 'SSL'],
    featured: false,
    img: '/photo5.png',
    gradient: 'linear-gradient(135deg, #0a180a 0%, #080e08 50%, #080808 100%)',
    accentColor: 'rgba(80,180,80,0.05)',
    link: null,
    year: '2024',
    status: 'Ongoing',
  },
  {
    id: 'marketing',
    tag: 'Digital Marketing · 4 Businesses',
    category: 'marketing',
    title: 'Digital Marketing Operations',
    desc: 'Currently managing full digital presence for 4 active business shops — overseeing social media strategy, content creation, Meta ad campaigns, and performance analytics.',
    tech: ['Meta Ads', 'Instagram', 'Content', 'Analytics'],
    featured: false,
    img: '/photo4.jpg',
    gradient: 'linear-gradient(135deg, #180a0a 0%, #0e0808 50%, #080808 100%)',
    accentColor: 'rgba(180,80,80,0.05)',
    link: null,
    year: '2024',
    status: 'Active',
  },
];

const filters = [
  { id: 'all', label: 'All Projects' },
  { id: 'development', label: 'Development' },
  { id: 'ecommerce', label: 'E-commerce' },
  { id: 'devops', label: 'DevOps' },
  { id: 'marketing', label: 'Marketing' },
];

const statusColors = {
  Live: { bg: 'rgba(80,200,80,0.1)', dot: '#50c850', text: 'rgba(80,200,80,0.9)' },
  Delivered: { bg: 'rgba(201,169,110,0.1)', dot: '#c9a96e', text: 'rgba(201,169,110,0.9)' },
  Ongoing: { bg: 'rgba(80,160,255,0.1)', dot: '#50a0ff', text: 'rgba(80,160,255,0.9)' },
  Active: { bg: 'rgba(255,160,60,0.1)', dot: '#ffa03c', text: 'rgba(255,160,60,0.9)' },
};

/* ── 3D Tilt Wrapper ── */
function TiltCard({ children, style, className, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 90, damping: 18 });
  const sry = useSpring(ry, { stiffness: 90, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    rx.set(-(((e.clientY - r.top) / r.height) - 0.5) * 5);
    ry.set((((e.clientX - r.left) / r.width) - 0.5) * 5);
  };
  const onLeave = () => { rx.set(0); ry.set(0); onMouseLeave?.(); };

  return (
    <motion.div
      ref={ref}
      style={{ ...style, rotateX: srx, rotateY: sry, transformStyle: 'preserve-3d', perspective: 1000 }}
      className={className}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onMouseEnter={onMouseEnter}
    >
      {children}
    </motion.div>
  );
}

/* ── Project Card Component ── */
function ProjectCard({ project, delay }) {
  const { ref, controls } = useReveal();
  const [hovered, setHovered] = useState(false);
  const status = statusColors[project.status];

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay } }
      }}
      style={{ gridColumn: project.featured ? '1 / -1' : 'auto' }}
    >
      <TiltCard
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${hovered ? 'rgba(201,169,110,0.3)' : 'rgba(255,255,255,0.07)'}`,
          display: 'flex',
          flexDirection: project.featured ? 'row' : 'column',
          height: '100%',
          overflow: 'hidden',
          transition: 'border-color 0.4s ease',
          position: 'relative'
        }}
        className={project.featured ? 'featured-card' : 'standard-card'}
      >
        {/* IMAGE SECTION */}
        <div style={{ 
          flex: project.featured ? '1.3' : 'none', 
          height: project.featured ? 'auto' : '280px',
          order: project.featured ? 2 : 1,
          overflow: 'hidden',
          position: 'relative'
        }}>
          <motion.img
            src={project.img}
            alt={project.title}
            animate={{ scale: hovered ? 1.05 : 1 }}
            transition={{ duration: 0.6 }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,8,8,0.5), transparent)' }} />
        </div>

        {/* CONTENT SECTION */}
        <div style={{ 
          flex: project.featured ? '1' : 'none',
          padding: project.featured ? '50px' : '35px', 
          display: 'flex', flexDirection: 'column', 
          justifyContent: 'center',
          order: project.featured ? 1 : 2
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <span style={{ color: '#c9a96e', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase' }}>{project.tag}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: status.bg, padding: '4px 10px', border: `1px solid ${status.dot}44` }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: status.dot }} />
                <span style={{ fontSize: '9px', color: status.text, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>{project.status}</span>
            </div>
          </div>

          {/* TITLE AS LINK */}
          {project.link ? (
            <a href={project.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <h3 style={{ 
                fontFamily: "'Cormorant Garamond', serif", fontSize: project.featured ? '36px' : '26px', 
                marginBottom: '20px', color: hovered ? '#c9a96e' : '#fff', transition: 'color 0.3s ease',
                lineHeight: 1.2
              }}>
                {project.title} <span style={{ fontSize: '18px', verticalAlign: 'middle', opacity: 0.5 }}>↗</span>
              </h3>
            </a>
          ) : (
            <h3 style={{ 
              fontFamily: "'Cormorant Garamond', serif", fontSize: project.featured ? '36px' : '26px', 
              marginBottom: '20px', color: '#fff', lineHeight: 1.2 
            }}>
              {project.title}
            </h3>
          )}
          
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.7', marginBottom: '30px' }}>
            {project.desc}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {project.tech.map(t => (
              <span key={t} style={{ fontSize: '9px', padding: '5px 12px', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </TiltCard>
    </motion.div>
  );
}

/* ── Main Projects Section ── */
export default function Projects() {
  const [activeFilter, setActiveFilter] = useState('all');
  const filtered = activeFilter === 'all' ? projects : projects.filter(p => p.category === activeFilter);

  return (
    <section id="projects" style={{ background: '#080808', padding: '120px 8vw' }}>
      <div style={{ marginBottom: '80px' }}>
        <h2 style={{ fontSize: '48px', color: '#fff', fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
          Selected <em>Projects</em>
        </h2>
        
        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '40px', flexWrap: 'wrap' }}>
          {filters.map(f => {
            const active = activeFilter === f.id;
            return (
              <button 
                key={f.id} 
                onClick={() => setActiveFilter(f.id)}
                style={{
                  background: active ? '#c9a96e' : 'transparent',
                  color: active ? '#080808' : 'rgba(255,255,255,0.4)',
                  border: `1px solid ${active ? '#c9a96e' : 'rgba(255,255,255,0.1)'}`,
                  padding: '10px 24px', fontSize: '10px', cursor: 'pointer', transition: '0.3s',
                  textTransform: 'uppercase', letterSpacing: '2px', fontWeight: active ? 700 : 400
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div 
          key={activeFilter}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="projects-grid" 
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}
        >
          {filtered.map((p, i) => (
            <ProjectCard key={p.id} project={p} delay={i * 0.1} />
          ))}
        </motion.div>
      </AnimatePresence>

      <style>{`
        @media (max-width: 1100px) {
          .featured-card { flex-direction: column !important; }
          .featured-card > div { width: 100% !important; height: 350px !important; }
        }
        @media (max-width: 850px) {
          .projects-grid { grid-template-columns: 1fr !important; }
          section#projects { padding: 80px 24px !important; }
        }
      `}</style>
    </section>
  );
}